import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { AlertTriangle, MapPin, Sparkles, ShieldCheck, UtensilsCrossed, ArrowRight } from "lucide-react";
import { useCountryFilter } from "@/lib/useCountryFilter";
import CountryFilterBanner from "@/components/CountryFilterBanner";
import { paletteFor } from "@/lib/palette";

const URGENCY_STYLE = {
  emergency: { bg: "#B23A3A", label: "Emergency" },
  high: { bg: "#B25D38", label: "High" },
  normal: { bg: "var(--wofy-primary)", label: "Normal" },
  low: { bg: "#7A7A7A", label: "Low" },
};

const BADGE_STYLE = {
  official: { bg: "var(--wofy-secondary)", label: "OFFICIAL SOCIAL PULSE POST", icon: Sparkles },
  verified_org: { bg: "var(--wofy-primary)", label: "VERIFIED ORGANIZATION", icon: ShieldCheck },
  verified_authority: { bg: "#5C6B52", label: "VERIFIED BY AUTHORITY", icon: ShieldCheck },
  self_reported: { bg: "#7A7A7A", label: "SELF-REPORTED", icon: null },
};

export default function Causes() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState("all");
  const [urgency, setUrgency] = useState("all");

  useEffect(() => {
    http.get("/causes/categories").then((r) => setCats(r.data.categories)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (category !== "all") params.category = category;
    if (urgency !== "all") params.urgency = urgency;
    http.get("/causes", { params }).then((r) => setItems(r.data)).catch(() => {});
  }, [category, urgency]);

  const grouped = useMemo(() => {
    const g = { emergency: [], high: [], normal: [], low: [] };
    for (const c of items) (g[c.urgency] || g.normal).push(c);
    return g;
  }, [items]);

  const filtered = useCountryFilter(items, ["location.country", "location.city"]);
  const groupedFiltered = useMemo(() => {
    const g = { emergency: [], high: [], normal: [], low: [] };
    for (const c of filtered) (g[c.urgency] || g.normal).push(c);
    return g;
  }, [filtered]);

  return (
    <div data-testid="causes-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
      <div className="overline mb-3">COMMUNITY CAUSES</div>
      <h1 className="font-display text-4xl md:text-5xl max-w-2xl leading-[1.05]">Real people. Real needs. Real help.</h1>
      <p className="mt-4 max-w-2xl text-sm" style={{ color: "var(--wofy-text-soft)" }}>
        Every cause below has been reviewed by our moderation team. Browse by category or urgency and reach out to those you can help.
      </p>

      <div className="mt-8 flex flex-wrap gap-2">
        <button data-testid="cause-filter-all" onClick={() => setCategory("all")} className={`px-3 py-1.5 text-sm rounded-full ${category === "all" ? "" : "hover:opacity-80"}`} style={category === "all" ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : { border: "1px solid var(--wofy-border)" }}>All</button>
        {cats.map((c) => (
          <button key={c.key} onClick={() => setCategory(c.key)} data-testid={`cause-filter-${c.key}`} className={`px-3 py-1.5 text-sm rounded-full ${category === c.key ? "" : "hover:opacity-80"}`} style={category === c.key ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : { border: "1px solid var(--wofy-border)" }}>{c.label}</button>
        ))}
      </div>

      {category === "food" && (
        <Link
          data-testid="causes-recipes-banner"
          to="/recipes"
          className="mt-6 block rounded-lg overflow-hidden border transition hover:shadow-lg"
          style={{ borderColor: "var(--wofy-line)", background: "linear-gradient(135deg, #c47f3a 0%, #a86828 100%)", color: "white" }}
        >
          <div className="grid grid-cols-[80px_1fr_auto] gap-4 items-center p-5">
            <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: "rgba(255,255,255,0.15)" }}>
              <UtensilsCrossed size={40} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest opacity-90 mb-1">RECIPES OF THE DAY</div>
              <div className="font-display text-2xl leading-tight">Feed a family with recipes from our community.</div>
              <div className="text-sm opacity-90 mt-1">Browse over 20 traditional and modern recipes from around the world — or share yours.</div>
            </div>
            <div className="inline-flex items-center gap-2 font-semibold text-sm whitespace-nowrap">
              Open recipes <ArrowRight size={14} />
            </div>
          </div>
        </Link>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {["all", "emergency", "high", "normal", "low"].map((u) => (
          <button key={u} onClick={() => setUrgency(u)} className={`px-3 py-1 text-xs rounded-full uppercase font-mono ${urgency === u ? "text-white" : ""}`} style={urgency === u ? { background: u === "all" ? "var(--wofy-text)" : (URGENCY_STYLE[u]?.bg || "#666") } : { border: "1px solid var(--wofy-border)" }}>{u}</button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="mt-16 wofy-card text-center py-16">
          <div className="font-display text-2xl">No causes yet.</div>
          <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            Be the first to <Link to="/submit-cause" className="underline">share yours</Link> — moderators respond within 24 hours.
          </p>
        </div>
      ) : (
        <>
        <CountryFilterBanner shown={filtered.length} total={items.length} noun="causes" />
        <div className="mt-10 space-y-14">
          {["emergency", "high", "normal", "low"].map((lvl) => groupedFiltered[lvl].length > 0 && (
            <section key={lvl}>
              <div className="flex items-center gap-2">
                <span className="inline-block px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider text-white" style={{ background: URGENCY_STYLE[lvl].bg }}>{URGENCY_STYLE[lvl].label}</span>
                <span className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>{groupedFiltered[lvl].length} cause{groupedFiltered[lvl].length > 1 ? "s" : ""}</span>
              </div>
              <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {groupedFiltered[lvl].map((c) => <CauseCard key={c.id} c={c} />)}
              </div>
            </section>
          ))}
        </div>
        </>
      )}
    </div>
  );
}

function CauseCard({ c }) {
  const badge = BADGE_STYLE[c.verification_badge] || BADGE_STYLE.self_reported;
  const Ic = badge.icon;
  const palette = paletteFor(c.id || c.title);
  return (
    <Link
      to={`/causes/${c.id}`}
      data-testid={`cause-card-${c.id}`}
      className="rounded-2xl overflow-hidden group block border transition-all hover:-translate-y-0.5 hover:shadow-lg"
      style={{ borderColor: `${palette.accent}33`, background: `linear-gradient(135deg, ${palette.from} 0%, ${palette.to} 100%)` }}
    >
      <div className="aspect-[4/3] overflow-hidden">
        {c.photos?.[0] ? (
          <img src={c.photos[0]} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${palette.accent}22, ${palette.accent}55)` }}
          >
            <AlertTriangle className="w-10 h-10" style={{ color: palette.accent }} />
          </div>
        )}
      </div>
      <div className="p-6">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider text-white" style={{ background: badge.bg }}>
            {Ic && <Ic className="w-3 h-3" />} {badge.label}
          </span>
          <span
            className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded"
            style={{ background: `${palette.accent}22`, color: palette.ink }}
          >{c.category}</span>
        </div>
        <h3 className="font-display text-xl leading-tight" style={{ color: palette.ink }}>{c.title}</h3>
        <p className="mt-2 text-sm line-clamp-2" style={{ color: palette.ink, opacity: 0.75 }}>{c.description}</p>
        <div className="mt-3 text-[11px] font-mono flex items-center gap-2" style={{ color: palette.ink, opacity: 0.7 }}>
          <MapPin className="w-3 h-3" /> {c.location.city ? `${c.location.city}, ` : ""}{c.location.country}
        </div>
      </div>
    </Link>
  );
}
